/**
 * 🗃 NAS 파일탐색기 — /nas-files (v2.114.0 · 2026-09-02)
 *
 * 사내 NAS 공유 폴더를 포털 안에서 열어 보는 화면. 접속은 **본인 NAS 계정**으로 하므로
 * 「무엇이 보이나·무엇을 할 수 있나」는 포털 권한이 아니라 **NAS 가 그 계정에 준 권한**이
 * 그대로 경계가 된다. 그래서 이 화면은 역할(role)로 갈라 두지 않았다 — 포털이 권한을 두 벌로
 * 들고 있으면 두 벌이 어긋나는 날 사고가 난다.
 *
 * ⚠ 「목록이 비어 보이는 것」과 「계정이 없는 것」은 전혀 다른 사건이라 빈 표로 뭉뚱그리지 않는다.
 *   연결 전(connected=false)·서버 미준비(ready=false)에는 표 대신 안내판을 띄운다.
 *
 * ⚠ NAS 가 목록에 주는 `..` 는 **절대 따라가지 않는다**(서버가 걸러 내지만 화면도 한 겹 막는다).
 *   위로 가기는 우리가 들고 있는 path 배열에서만 만든다 — 여기가 경로 탈출이 생길 자리다.
 *
 * ⭐ LastUpdateBadge 는 안 붙였다. 그 배지는 **운영 DB 동기화 시각**을 말하는데 여기 목록은
 *   누를 때마다 NAS 에서 새로 읽어 오는 값이라 같이 두면 뜻이 헷갈린다(대신 [새로고침]).
 *
 * 서버 규칙 중 화면이 반드시 비춰야 하는 것 (backend/src/routes/nas/browse.ts):
 *   · 폴더는 **비어 있을 때만** 지워진다 → 확인 문구에 미리 적고, 남은 개수는 서버 문구를 그대로 보여준다.
 *   · 「포털보관문서」 안의 것은 **지우기·이름 바꾸기 금지**(보기·내려받기·올리기는 됨) → 두 버튼을 감춘다.
 *   · 올리기는 **덮어쓰지 않는다** → 이름을 비켜 저장했으면(renamed) 그 사실을 반드시 알린다.
 *
 * v2.313.0 (개발요청 #26) — 파일 이름을 누르면 **미리보기 창**(전역 PdfPreviewModal · 첨부 미리보기와 같은 창).
 *   그림·PDF·엑셀은 그대로, 워드·파워포인트는 서버가 PDF 로 바꿔 보여 준다(몇 초) · 한글·압축은 「미리보기 없음 + 저장」 안내.
 *   줄 앞 체크박스로 파일을 골라 [✉ 메일 보내기] → 메일 쓰기 창이 그 파일들을 붙인 채 열린다(보낼 때 서버가 본인 NAS 계정으로 가져온다).
 *   고른 것은 **이 폴더 안에서만** 유지된다 — 폴더를 옮기면 비운다(다른 폴더 것까지 모으려면 메일 창의 [+ NAS 파일]).
 */

import { Fragment, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  browseNas,
  createNasFolder,
  deleteNasEntry,
  downloadNasFile,
  fetchNasAccount,
  renameNasEntry,
  uploadToNas,
  nasMailDocRef,
  nasPreviewKind,
  nasPreviewTarget,
  NAS_PREVIEW_MAX_BYTES,
  type NasEntry,
} from '../lib/nas-api';
import { openPdfPreview } from '../lib/pdf-preview';
import { openMailCompose } from '../lib/mail-compose';
import { LoadingLines } from '../components/ui/Skeleton';

/** 메일 한도 — 서버 lib/mail-send/validate.ts 와 같은 값 */
const MAIL_MAX_BYTES = 25 * 1024 * 1024;
const MAIL_MAX_COUNT = 20;

/** 포털이 문서를 자동으로 쌓는 폴더 — 서버 browse-guard.PROTECTED_FOLDER 와 같은 이름이어야 한다. */
const PROTECTED_FOLDER = '포털보관문서';
/**
 * 포털이 지우기·이름 바꾸기를 거절하는 폴더들 — 서버 browse-guard 와 같은 목록이어야 한다.
 * v2.224.0: 📷 「포털 재고실사 사진」(실사 사진 · 포털 DB 가 파일 위치를 적어 둔다) 추가.
 */
const PROTECTED_FOLDERS = [PROTECTED_FOLDER, '포털 재고실사 사진'];

/** 사람이 읽는 크기. 10 미만일 때만 소수 한 자리 — 자릿수가 들쭉날쭉하면 오른쪽 정렬이 흐트러진다. */
function fmtSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** 서버는 해석에 성공하면 ISO, 실패하면 NAS 원문("Wed Aug 26 10:12:33 2026")을 준다. */
function fmtWhen(value: string): string {
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return value;
  const d = new Date(t);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const BTN = 'px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded disabled:opacity-50';
const CHIP = 'px-2 py-1 text-xs rounded bg-slate-100 hover:bg-slate-200 text-slate-600';

export function NasExplorerPage() {
  const qc = useQueryClient();
  const [path, setPath] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  /** ✉ 메일로 보낼 파일 — 이 폴더 안의 이름 (v2.313.0) */
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const account = useQuery({ queryKey: ['nas', 'account'], queryFn: fetchNasAccount });
  const acc = account.data;
  const usable = !!acc?.connected && !!acc.ready;

  const listing = useQuery({
    queryKey: ['nas', 'browse', path],
    queryFn: () => browseNas(path),
    // 계정이 없거나 서버가 준비 안 된 상태에서 부르면 매번 실패 배너만 쌓인다 — 안내판을 먼저 보여준다.
    enabled: usable,
  });

  const invalidateHere = (): void => {
    void qc.invalidateQueries({ queryKey: ['nas', 'browse', path] });
  };
  // 서버 오류 문구는 이미 직원이 읽을 한국어라 그대로 띄운다(가공하면 사유가 뭉개진다).
  const onFail = (e: unknown): void => setFeedback({ kind: 'err', text: (e as Error).message });

  const mkdirMut = useMutation({
    mutationFn: (name: string) => createNasFolder(path, name),
    onSuccess: (r) => {
      setFeedback({ kind: 'ok', text: `폴더 「${r.name}」 을(를) 만들었습니다.` });
      invalidateHere();
    },
    onError: onFail,
  });

  const renameMut = useMutation({
    mutationFn: (v: { name: string; newName: string }) => renameNasEntry(path, v.name, v.newName),
    onSuccess: (r, v) => {
      // 옛 이름이 고른 목록에 남으면 나중에 같은 이름의 다른 파일이 몰래 골라진다
      setChecked((prev) => { const next = new Set(prev); next.delete(v.name); return next; });
      setFeedback({ kind: 'ok', text: `이름을 「${v.name}」 → 「${r.name}」 (으)로 바꿨습니다.` });
      invalidateHere();
    },
    onError: onFail,
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => deleteNasEntry(path, name),
    onSuccess: (r) => {
      setChecked((prev) => { const next = new Set(prev); next.delete(r.name); return next; });
      setFeedback({ kind: 'ok', text: `${r.kind === 'dir' ? '폴더' : '파일'} 「${r.name}」 을(를) 삭제했습니다.` });
      invalidateHere();
    },
    onError: onFail,
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadToNas(path, file),
    onSuccess: (r) => {
      setFeedback({
        kind: 'ok',
        text: r.renamed
          ? `같은 이름의 파일이 이미 있어 「${r.savedName}」 (으)로 저장했습니다. (기존 파일은 그대로 둡니다)`
          : `「${r.savedName}」 을(를) 올렸습니다.`,
      });
      invalidateHere();
    },
    onError: onFail,
  });

  const busy = mkdirMut.isPending || renameMut.isPending || deleteMut.isPending || uploadMut.isPending;

  /** 경로 이동 — 찾기 낱말은 폴더가 바뀌면 뜻이 없어지므로 같이 비운다. */
  const go = (next: string[]): void => {
    setPath(next);
    setKeyword('');
    setChecked(new Set());
  };

  /** 이름 누르기 = 미리보기 (v2.313.0) — 너무 큰 파일은 받기 전에 안내 */
  const onPreview = (e: NasEntry): void => {
    if (nasPreviewKind(e.name) !== 'none' && e.size > NAS_PREVIEW_MAX_BYTES) {
      setFeedback({
        kind: 'err',
        text: `「${e.name}」 은(는) ${fmtSize(e.size)} 라 미리보기를 하지 않습니다(${NAS_PREVIEW_MAX_BYTES / 1024 / 1024}MB 까지) — [내려받기]로 열어 주세요.`,
      });
      return;
    }
    openPdfPreview(nasPreviewTarget(path, e.name));
  };

  const enterFolder = (name: string): void => {
    // ⚠ `.`·`..` 는 서버가 목록에서 이미 빼지만, 화면에서도 막는다 — 여기가 경로 탈출 통로다.
    if (name === '.' || name === '..') return;
    go([...path, name]);
  };

  const onNewFolder = (): void => {
    const name = window.prompt('새 폴더 이름을 입력하세요.');
    if (name === null) return;
    if (!name.trim()) return;
    mkdirMut.mutate(name.trim());
  };

  const onRename = (e: NasEntry): void => {
    const next = window.prompt('새 이름을 입력하세요.', e.name);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === e.name) return;
    renameMut.mutate({ name: e.name, newName: trimmed });
  };

  const onDelete = (e: NasEntry): void => {
    const kind = e.isDir ? '폴더' : '파일';
    // 폴더는 서버가 「비어 있을 때만」 지운다 — 눌러 보고 거절당하기 전에 미리 알려 준다.
    const note = e.isDir ? '\n\n※ 폴더는 안이 비어 있어야 지워집니다.' : '';
    if (!window.confirm(`${kind} 「${e.name}」 을(를) 삭제할까요?\n삭제하면 되돌릴 수 없습니다.${note}`)) return;
    deleteMut.mutate(e.name);
  };

  const onPickFile = (ev: ChangeEvent<HTMLInputElement>): void => {
    const file = ev.target.files?.[0];
    // ⚠값을 비워 두지 않으면 **같은 파일을 다시 고를 때** onChange 가 안 돈다(실패 후 재시도가 막힌다).
    ev.target.value = '';
    if (!file) return;
    uploadMut.mutate(file);
  };

  const entries = listing.data?.entries ?? [];
  const kw = keyword.trim().toLowerCase();
  const rows = kw ? entries.filter((e) => e.name.toLowerCase().includes(kw)) : entries;
  /** 이 경로가 자동보관 폴더에 걸려 있나 — 걸리면 그 안의 모든 항목이 수정 잠김이다. */
  const inProtected = path.some((s) => PROTECTED_FOLDERS.includes(s));
  // ✉ 메일 — 고른 파일(목록에서 사라진 이름은 빼고 센다)
  const pickedFiles = entries.filter((e) => !e.isDir && checked.has(e.name));
  const pickedBytes = pickedFiles.reduce((a, e) => a + e.size, 0);
  const visibleFiles = rows.filter((e) => !e.isDir);
  const allVisibleChecked = visibleFiles.length > 0 && visibleFiles.every((e) => checked.has(e.name));
  const toggleOne = (name: string): void => {
    const next = new Set(checked);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setChecked(next);
  };
  const toggleAllVisible = (): void => {
    const next = new Set(checked);
    if (allVisibleChecked) visibleFiles.forEach((e) => next.delete(e.name));
    else visibleFiles.forEach((e) => next.add(e.name));
    setChecked(next);
  };
  const onMail = (): void => {
    if (pickedFiles.length === 0) return;
    if (pickedFiles.length > MAIL_MAX_COUNT) {
      setFeedback({ kind: 'err', text: `메일 첨부는 한 번에 ${MAIL_MAX_COUNT}개까지입니다 — 지금 ${pickedFiles.length}개를 골랐습니다.` });
      return;
    }
    if (pickedBytes > MAIL_MAX_BYTES) {
      setFeedback({
        kind: 'err',
        text: `고른 파일이 모두 ${fmtSize(pickedBytes)} 라 메일 한도 25MB 를 넘습니다 — 몇 개를 빼거나, 받는 분께 NAS 위치를 알려 주세요.`,
      });
      return;
    }
    openMailCompose({ docRefs: pickedFiles.map((e) => nasMailDocRef(path, e)) });
  };

  const header = (
    <div className="mb-5">
      <h1 className="ui-page-title">NAS 파일탐색기</h1>
      <p className="ui-page-sub">
        본인 NAS 계정으로 접속합니다 — <b>보이는 폴더와 할 수 있는 일은 그 계정에 주어진 NAS 권한이 그대로 정합니다.</b>
        {acc?.host && acc.share ? (
          <span className="ml-2 text-slate-400">{`\\\\${acc.host}\\${acc.share}`}</span>
        ) : null}
      </p>
    </div>
  );

  if (account.isLoading) {
    return (
      <div className="p-4 md:p-8">
        {header}
        <div className="py-16 text-center text-slate-400">확인 중…</div>
      </div>
    );
  }

  if (account.isError) {
    return (
      <div className="p-4 md:p-8">
        {header}
        <div className="px-3 py-2 rounded text-sm border bg-rose-50 border-rose-200 text-rose-800">
          {(account.error as Error).message}
        </div>
      </div>
    );
  }

  // ① 서버가 아직 탐색기를 못 돌리는 상태 (도구·열쇠 파일) — 직원이 계정을 다시 연결해 봐야 소용없다.
  if (acc && !acc.ready) {
    return (
      <div className="p-4 md:p-8">
        {header}
        <div className="bg-white rounded border border-slate-200 p-12 text-center">
          <div className="text-5xl mb-3">🚧</div>
          <div className="text-lg font-bold text-slate-700">지금은 파일탐색기를 쓸 수 없습니다</div>
          <p className="text-sm text-slate-500 mt-2">{acc.readyReason ?? '서버가 아직 준비되지 않았습니다.'}</p>
        </div>
      </div>
    );
  }

  // ② 계정 미연결 — 빈 표를 보여주면 「폴더가 하나도 없다」로 읽힌다. 반드시 안내판으로 가른다.
  if (!acc?.connected) {
    return (
      <div className="p-4 md:p-8">
        {header}
        <div className="bg-white rounded border border-slate-200 p-12 text-center">
          <div className="text-5xl mb-3">🔒</div>
          <div className="text-lg font-bold text-slate-700">NAS 계정을 먼저 연결해 주세요</div>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            파일탐색기는 포털 계정이 아니라 <b>본인 NAS 계정</b>으로 접속합니다.
            <br />
            <Link to="/mypage" className="text-blue-600 underline">
              마이페이지 › NAS 계정 연결
            </Link>
            {' '}에서 NAS 아이디·비밀번호를 등록하면 이 화면에서 폴더를 열 수 있습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {header}

      {/* 로그인이 한 번 실패한 뒤로 표시가 남아 있는 경우 — 비밀번호를 바꿨을 때가 대부분이다 */}
      {acc.status === 'ERROR' && (
        <div className="mb-3 px-3 py-2 rounded text-sm border bg-amber-50 border-amber-200 text-amber-800">
          마지막 접속이 실패했습니다{acc.lastError ? ` — ${acc.lastError}` : ''}. NAS 비밀번호를 바꾸셨다면{' '}
          <Link to="/mypage" className="underline">마이페이지 › NAS 계정 연결</Link> 에서 다시 연결해 주세요.
        </div>
      )}

      {feedback && (
        <div
          className={`mb-3 px-3 py-2 rounded text-sm border flex items-start gap-2 ${
            feedback.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          <span className="flex-1 whitespace-pre-wrap">{feedback.text}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            className="shrink-0 text-xs opacity-60 hover:opacity-100"
          >
            닫기 ✕
          </button>
        </div>
      )}

      {/* 현재 위치 — NAS 가 준 `..` 가 아니라 우리가 들고 있는 path 로만 움직인다 */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <button type="button" onClick={() => go([])} className={CHIP}>
          공유 루트
        </button>
        {path.map((seg, i) => (
          <Fragment key={`${i}-${seg}`}>
            <span className="text-slate-300">›</span>
            {i === path.length - 1 ? (
              <span className="px-2 py-1 text-xs rounded bg-slate-700 text-white font-medium">{seg}</span>
            ) : (
              <button type="button" onClick={() => go(path.slice(0, i + 1))} className={CHIP}>
                {seg}
              </button>
            )}
          </Fragment>
        ))}
        <button
          type="button"
          onClick={() => go(path.slice(0, -1))}
          disabled={path.length === 0}
          className="btn btn-secondary btn-sm ml-2"
        >
          ↑ 위로
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        <button type="button" onClick={() => void listing.refetch()} disabled={listing.isFetching} className={BTN}>
          ↻ 새로고침
        </button>
        <button type="button" onClick={onNewFolder} disabled={busy} className={BTN}>
          ＋ 새 폴더
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={BTN}>
          {uploadMut.isPending ? '올리는 중…' : '⬆ 파일 올리기'}
        </button>
        <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" />
        <button
          type="button"
          onClick={onMail}
          disabled={pickedFiles.length === 0}
          title="줄 앞 체크박스로 고른 파일을 붙여 메일 쓰기 창을 엽니다 — 보낼 때 서버가 본인 NAS 계정으로 가져와 붙입니다 (25MB·20개까지)"
          className="px-3 py-1.5 text-sm border border-sky-300 text-sky-700 hover:bg-sky-50 rounded disabled:opacity-50 disabled:hover:bg-transparent"
        >
          ✉ 메일 보내기{pickedFiles.length ? ` (${pickedFiles.length}개 · ${fmtSize(pickedBytes)})` : ''}
        </button>

        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="이 폴더에서 이름 찾기"
            className="ui-input w-56"
          />
          {keyword && (
            <button type="button" onClick={() => setKeyword('')} className="text-xs text-slate-500 hover:text-slate-700">
              지우기 ✕
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-2">
        🔎 찾기는 <b>지금 열린 폴더 안에서만</b> 걸러 냅니다 (하위 폴더까지 뒤지지 않습니다).
        {kw ? ` — ${entries.length}개 중 ${rows.length}개` : ''}
      </p>

      {listing.isLoading ? (
        <div className="py-16 text-center text-slate-400"><LoadingLines /></div>
      ) : listing.isError ? (
        <div className="px-3 py-2 rounded text-sm border bg-rose-50 border-rose-200 text-rose-800">
          {(listing.error as Error).message}
          <button
            type="button"
            onClick={() => void listing.refetch()}
            className="ml-2 underline"
          >
            다시 시도
          </button>
        </div>
      ) : (
        <div className="bg-white rounded border border-slate-200 overflow-x-auto">
          <table className="data-table w-full text-[13px]">
            <thead>
              <tr>
                <th className="w-8 text-center">
                  <input
                    type="checkbox"
                    checked={allVisibleChecked}
                    disabled={visibleFiles.length === 0}
                    onChange={toggleAllVisible}
                    title="이 목록의 파일 모두 고르기(폴더는 빼고)"
                    aria-label="파일 모두 고르기"
                  />
                </th>
                <th className="text-left">이름</th>
                <th className="text-right whitespace-nowrap">크기</th>
                <th className="text-left whitespace-nowrap">수정한 날짜</th>
                <th className="text-left whitespace-nowrap">작업</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-slate-400 py-10">
                    {entries.length === 0
                      ? '이 폴더에는 항목이 없습니다.'
                      : `「${keyword.trim()}」 와(과) 맞는 이름이 이 폴더에 없습니다.`}
                  </td>
                </tr>
              ) : (
                rows.map((e) => {
                  // 숨김·시스템 파일 — 지우지는 않고 눈에 덜 띄게만 (NAS 창과 같은 감각)
                  const dimmed = e.attrs.includes('H') || e.attrs.includes('S');
                  // 자동보관 폴더 자신 + 그 안의 모든 것 = 지우기·이름 바꾸기 잠김 (서버가 거절한다)
                  const locked = inProtected || PROTECTED_FOLDERS.includes(e.name);
                  return (
                    <tr key={e.name} className={`hover:bg-slate-50 ${dimmed ? 'text-slate-400' : 'text-slate-700'}`}>
                      <td className="w-8 text-center">
                        {!e.isDir && (
                          <input
                            type="checkbox"
                            checked={checked.has(e.name)}
                            onChange={() => toggleOne(e.name)}
                            aria-label={`${e.name} 고르기`}
                          />
                        )}
                      </td>
                      <td>
                        <span className="mr-1.5">{e.isDir ? '📁' : '📄'}</span>
                        {e.isDir ? (
                          <button
                            type="button"
                            onClick={() => enterFolder(e.name)}
                            className={`font-medium hover:underline ${dimmed ? 'text-slate-400' : 'text-blue-600'}`}
                          >
                            {e.name}
                          </button>
                        ) : (
                          // 이름 = 미리보기 (v2.313.0) — 미리보기가 없는 형식은 창이 「저장」을 안내한다
                          <button
                            type="button"
                            onClick={() => onPreview(e)}
                            title={nasPreviewKind(e.name) === 'none' ? '눌러서 열기 — 이 형식은 미리보기가 없어 저장 안내가 뜹니다' : nasPreviewKind(e.name) === 'office' ? '눌러서 미리보기 — 워드·파워포인트는 PDF 로 바꿔 보여 줍니다(몇 초)' : '눌러서 미리보기'}
                            className="text-left hover:text-blue-700 hover:underline"
                          >
                            {e.name}
                          </button>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap tabular-nums">
                        {e.isDir ? '-' : fmtSize(e.size)}
                      </td>
                      <td className="whitespace-nowrap">{fmtWhen(e.modified)}</td>
                      <td className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {!e.isDir && (
                            <button
                              type="button"
                              onClick={() => downloadNasFile(path, e.name)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              내려받기
                            </button>
                          )}
                          {locked ? (
                            <span
                              className="text-xs text-slate-500"
                              title={`「${PROTECTED_FOLDER}」(견적서·발주서 자동 보관)·「포털 재고실사 사진」(실사 사진)은 포털이 쓰는 폴더라 지우기·이름 바꾸기가 막혀 있습니다. (보기·내려받기·올리기는 됩니다 · 사진 지우기는 실사 화면에서)`}
                            >
                              포털 보관 — 수정 잠김
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => onRename(e)}
                                disabled={busy}
                                className="text-xs text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-40"
                              >
                                이름 변경
                              </button>
                              <button
                                type="button"
                                onClick={() => onDelete(e)}
                                disabled={busy}
                                className="text-xs text-rose-600 hover:underline disabled:opacity-40"
                              >
                                삭제
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
